import { z } from 'zod';
import { CustomSandboxSizeLimitsSchema } from '@runloop/reflex-contract';

/**
 * Platform-wide maintenance mode. When `enabled`, every client renders a
 * persistent banner above the main content carrying `message`. The app stays
 * fully usable; the banner only clears when an admin disables the mode. Stored
 * as key/value config server-side and served both on `GET /config` (so fresh
 * loads see current state) and pushed over the WebSocket (so already-connected
 * clients update live without a refresh).
 */
export const MaintenanceStateSchema = z
  .object({
    enabled: z.boolean(),
    message: z.string(),
  })
  .meta({ id: 'MaintenanceState' });
export type MaintenanceState = z.infer<typeof MaintenanceStateSchema>;

/**
 * Public runtime config served at `GET /config`: the Runloop tunnel and
 * platform domains derived from `RUNLOOP_BASE_URL`. Read by the web/plugins to
 * build devbox tunnel URLs without hardcoding the host. Tagged so the OpenAPI
 * generator emits one reusable `PublicConfig` component.
 */
export const PublicConfigSchema = z
  .object({
    tunnelDomain: z.string(),
    platformUrl: z.string(),
    /**
     * Web origin for the deployment's GitHub host, e.g. `https://github.com`
     * or, on a GitHub Enterprise Server deployment, `https://github.acme.com`.
     * The web/plugins build repository links from this instead of hardcoding
     * `github.com`, which points enterprise users at the wrong host. Derived
     * server-side from `GITHUB_ENTERPRISE_URL` (see `getGitHubEndpoints`).
     */
    githubWebBaseUrl: z.string(),
    /**
     * Effective maximums for org-defined custom sandbox sizes, resolved
     * server-side from deployment defaults and Reflex service settings.
     * Optional in the wire schema so cached clients and legacy fixtures
     * without the field still validate; consumers fall back to
     * `DEFAULT_CUSTOM_SANDBOX_SIZE_LIMITS` when absent.
     */
    customSandboxSizeLimits: CustomSandboxSizeLimitsSchema.optional(),
    /** Current platform maintenance-mode state (see {@link MaintenanceStateSchema}). */
    maintenance: MaintenanceStateSchema,
  })
  .meta({ id: 'PublicConfig' });
export type PublicConfig = z.infer<typeof PublicConfigSchema>;
